import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { handleAsyncThunkError } from "redux/reduxHelpers";

const base = (projectId, resource) => `/crm/projects/${projectId}/${resource}`;

// ── Activities ────────────────────────────────────────────────────────────────

export const getActivities = createAsyncThunk(
  "ProjectActivity/getActivities",
  async ({ projectId, page = 1, perPage = 20, activityType, q, activeOnly = true }, { rejectWithValue }) => {
    try {
      const params = { page, per_page: perPage };
      if (activityType) params.activity_type = activityType;
      if (q)            params.q = q;
      if (activeOnly)   params.active_only = 'true';
      const res = await apiAxios.get(base(projectId, "activities"), { params });
      return { ...res.data, page };
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to load activities")); }
  }
);

const buildFormData = (namespace, data, files) => {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (v !== null && v !== undefined) fd.append(`${namespace}[${k}]`, v);
  });
  if (files?.length) files.forEach(f => fd.append(`${namespace}[attachments][]`, f));
  return fd;
};

export const createActivity = createAsyncThunk(
  "ProjectActivity/createActivity",
  async ({ projectId, data, files }, { rejectWithValue }) => {
    try {
      const payload = files?.length ? buildFormData('activity', data, files) : { activity: data };
      const cfg     = files?.length ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
      return (await apiAxios.post(base(projectId, "activities"), payload, cfg)).data;
    }
    catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to create activity")); }
  }
);

export const updateActivity = createAsyncThunk(
  "ProjectActivity/updateActivity",
  async ({ projectId, id, data, files }, { rejectWithValue }) => {
    try {
      const payload = files?.length ? buildFormData('activity', data, files) : { activity: data };
      const cfg     = files?.length ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
      return (await apiAxios.put(`${base(projectId, "activities")}/${id}`, payload, cfg)).data;
    }
    catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to update activity")); }
  }
);

export const deleteActivity = createAsyncThunk(
  "ProjectActivity/deleteActivity",
  async ({ projectId, id }, { rejectWithValue }) => {
    try { await apiAxios.delete(`${base(projectId, "activities")}/${id}`); return id; }
    catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to delete activity")); }
  }
);

// ── Tasks ─────────────────────────────────────────────────────────────────────

export const getTasks = createAsyncThunk(
  "ProjectActivity/getTasks",
  async ({ projectId, page = 1, perPage = 20, q, activeOnly = true }, { rejectWithValue }) => {
    try {
      const params = { page, per_page: perPage };
      if (q)          params.q = q;
      if (activeOnly) params.active_only = 'true';
      const res = await apiAxios.get(base(projectId, "tasks"), { params });
      return { ...res.data, page };
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to load tasks")); }
  }
);

export const createTask = createAsyncThunk(
  "ProjectActivity/createTask",
  async ({ projectId, data, files }, { rejectWithValue }) => {
    try {
      const payload = files?.length ? buildFormData('task', data, files) : { task: data };
      const cfg     = files?.length ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
      return (await apiAxios.post(base(projectId, "tasks"), payload, cfg)).data;
    }
    catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to create task")); }
  }
);

export const updateTask = createAsyncThunk(
  "ProjectActivity/updateTask",
  async ({ projectId, id, data, files }, { rejectWithValue }) => {
    try {
      const payload = files?.length ? buildFormData('task', data, files) : { task: data };
      const cfg     = files?.length ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
      return (await apiAxios.put(`${base(projectId, "tasks")}/${id}`, payload, cfg)).data;
    }
    catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to update task")); }
  }
);

export const deleteTask = createAsyncThunk(
  "ProjectActivity/deleteTask",
  async ({ projectId, id }, { rejectWithValue }) => {
    try { await apiAxios.delete(`${base(projectId, "tasks")}/${id}`); return id; }
    catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to delete task")); }
  }
);

// ── Notes ─────────────────────────────────────────────────────────────────────

export const getNotes = createAsyncThunk(
  "ProjectActivity/getNotes",
  async ({ projectId, page = 1, perPage = 15, q }, { rejectWithValue }) => {
    try {
      const params = { page, per_page: perPage };
      if (q) params.q = q;
      const res = await apiAxios.get(base(projectId, "notes"), { params });
      return { ...res.data, page };
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to load notes")); }
  }
);

export const createNote = createAsyncThunk(
  "ProjectActivity/createNote",
  async ({ projectId, content }, { rejectWithValue }) => {
    try { return (await apiAxios.post(base(projectId, "notes"), { note: { content } })).data; }
    catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to create note")); }
  }
);

export const updateNote = createAsyncThunk(
  "ProjectActivity/updateNote",
  async ({ projectId, id, content }, { rejectWithValue }) => {
    try { return (await apiAxios.put(`${base(projectId, "notes")}/${id}`, { note: { content } })).data; }
    catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to update note")); }
  }
);

export const deleteNote = createAsyncThunk(
  "ProjectActivity/deleteNote",
  async ({ projectId, id }, { rejectWithValue }) => {
    try { await apiAxios.delete(`${base(projectId, "notes")}/${id}`); return id; }
    catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to delete note")); }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const paginatedResource = () => ({
  data:    [],
  meta:    null,   // { total_count, total_pages, page, per_page }
  loading: false,
});

const initialState = {
  activities: paginatedResource(),
  tasks:      paginatedResource(),
  notes:      paginatedResource(),
  submitting: false,
  error:      null,
};

const upsert = (arr, item) => {
  const idx = arr.findIndex(x => x.id === item.id);
  return idx >= 0 ? arr.map((x, i) => i === idx ? item : x) : [item, ...arr];
};

const applyPagedFulfilled = (slice, payload) => {
  slice.loading = false;
  // Numbered pagination: each fetch shows exactly one page → always replace.
  slice.data = payload.data || [];
  slice.meta = payload.meta || null;
};

const projectActivitySlice = createSlice({
  name: "crm_project_activity",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Activities
    builder
      .addCase(getActivities.pending, (s) => { s.activities.loading = true; s.error = null; })
      .addCase(getActivities.fulfilled, (s, a) => { applyPagedFulfilled(s.activities, a.payload); })
      .addCase(getActivities.rejected,  (s, a) => { s.activities.loading = false; s.error = a.payload; })
      .addCase(createActivity.pending,   s => { s.submitting = true; })
      .addCase(createActivity.fulfilled, (s, a) => { s.submitting = false; s.activities.data = [a.payload.data, ...s.activities.data]; })
      .addCase(createActivity.rejected,  (s, a) => { s.submitting = false; s.error = a.payload; })
      .addCase(updateActivity.fulfilled, (s, a) => { s.activities.data = upsert(s.activities.data, a.payload.data); })
      .addCase(deleteActivity.fulfilled, (s, a) => { s.activities.data = s.activities.data.filter(x => x.id !== a.payload); });

    // Tasks
    builder
      .addCase(getTasks.pending, (s) => { s.tasks.loading = true; s.error = null; })
      .addCase(getTasks.fulfilled, (s, a) => { applyPagedFulfilled(s.tasks, a.payload); })
      .addCase(getTasks.rejected,  (s, a) => { s.tasks.loading = false; s.error = a.payload; })
      .addCase(createTask.pending,   s => { s.submitting = true; })
      .addCase(createTask.fulfilled, (s, a) => { s.submitting = false; s.tasks.data = [a.payload.data, ...s.tasks.data]; })
      .addCase(createTask.rejected,  (s, a) => { s.submitting = false; s.error = a.payload; })
      .addCase(updateTask.fulfilled, (s, a) => { s.tasks.data = upsert(s.tasks.data, a.payload.data); })
      .addCase(deleteTask.fulfilled, (s, a) => { s.tasks.data = s.tasks.data.filter(x => x.id !== a.payload); });

    // Notes
    builder
      .addCase(getNotes.pending, (s) => { s.notes.loading = true; s.error = null; })
      .addCase(getNotes.fulfilled, (s, a) => { applyPagedFulfilled(s.notes, a.payload); })
      .addCase(getNotes.rejected,  (s, a) => { s.notes.loading = false; s.error = a.payload; })
      .addCase(createNote.pending,   s => { s.submitting = true; })
      .addCase(createNote.fulfilled, (s, a) => { s.submitting = false; s.notes.data = [a.payload.data, ...s.notes.data]; })
      .addCase(createNote.rejected,  (s, a) => { s.submitting = false; s.error = a.payload; })
      .addCase(updateNote.fulfilled, (s, a) => { s.notes.data = upsert(s.notes.data, a.payload.data); })
      .addCase(deleteNote.fulfilled, (s, a) => { s.notes.data = s.notes.data.filter(x => x.id !== a.payload); });
  },
});

export default projectActivitySlice.reducer;
